/* eslint-disable @next/next/no-img-element */

'use client';

import AsideWrapper from '@fe-news/components/Aside/AsideWrapper';
import { styled } from '@linaria/react';
import Link from 'next/link';
import { sendGTM, GTMEventKey, CustomEventName } from '@fe-cnyes/fe-common-gtm';
import LinkFactory from '@fe-common-utils/libs/link';

const REST_BUTTONS = new Set(['鉅亨送愛心', '活動講座', '電子報']);
const NEW_ICON_SIZE = 40;

const AnueItemWrapper = styled.div<{ isOnlyChild?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  margin-top: 12px;
  gap: 32px;
  justify-content: ${({ isOnlyChild }) => (isOnlyChild ? 'center' : 'flex-start')};

  :global(#modalAnueService) {
    padding-top: 4px;

    .section-aside-wrapper {
      padding-bottom: 8px;
    }

    h3 {
      padding-left: 22px;
    }
  }
`;

const AnueItem = styled(Link)`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;

  img {
    width: 38px;
    height: 38px;
  }

  p {
    font-size: 14px;
    letter-spacing: 0.5px;
    color: #515c69;
  }
`;

const anueServiceIcon = '/assets/icons/anueService.svg';

const ANUE_BOTTOM_SERVICE_GTM_SECTION = 'News_Timed_Popup_鉅亨服務';

enum AnueBottomServiceGtmClickItem {
  APP = 'APP',
  EDM = 'EDM',
  FACEBOOK = 'Facebook',
  YOUTUBE = 'Youtube',
  LINE = 'Line',
  HAO = '鉅亨號',
  LOVE = '鉅亨送愛心',
  LECTURE = '活動講座',
  EMAIL = '電子報'
}

type AnueBottomServiceGtmEvent = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC;
  [GTMEventKey.SECTION]: typeof ANUE_BOTTOM_SERVICE_GTM_SECTION;
  [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem;
};

const Tabs = [
  {
    icon: '/assets/icons/logo/anue-logo.svg',
    src: 'https://www.cnyes.com/mobile/index_app.htm',
    title: '鉅亨APP',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.APP
    }
  },
  {
    icon: '/assets/icons/service/anue-hao.svg',
    src: 'https://hao.cnyes.com/wall/recommend',
    title: '鉅亨號',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.HAO
    }
  },
  {
    icon: '/assets/icons/service/anue-email.svg',
    src: 'https://www.cnyes.com/newsletter',
    title: '訂閱鉅亨',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.EDM
    }
  },
  {
    icon: '/assets/icons/service/anue-facebook.svg',
    src: 'https://www.facebook.com/anuetw/',
    title: '鉅亨粉絲團',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.FACEBOOK
    }
  },
  {
    icon: '/assets/icons/service/anue-line.svg',
    src: 'https://campaign.cnyes.com/topics/anuesns/',
    title: '鉅亨群組',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.LINE
    }
  },
  {
    icon: '/assets/icons/service/anue-youtube.svg',
    src: 'https://www.youtube.com/channel/UCHzw1et1X2fdz1VsXLO8nlw',
    title: '追蹤鉅亨YT',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.YOUTUBE
    }
  },
  {
    icon: '/assets/icons/service/anue-pr.svg',
    src: 'https://campaign.cnyes.com/topics/love',
    title: '鉅亨送愛心',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.LOVE
    }
  },
  {
    icon: '/assets/icons/service/anue-seminar.svg',
    src: `${LinkFactory.wwwChannelLink}/events`,
    title: '活動講座',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.LECTURE
    }
  },
  {
    icon: '/assets/icons/service/anue-edm.svg',
    src: `${LinkFactory.wwwChannelLink}/newsletter`,
    title: '電子報',
    gtmEvent: {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: ANUE_BOTTOM_SERVICE_GTM_SECTION,
      [GTMEventKey.CLICK_ITEM]: AnueBottomServiceGtmClickItem.EMAIL
    }
  }
];

const onClick = (gtmEvent: AnueBottomServiceGtmEvent) => {
  if (gtmEvent) {
    sendGTM(gtmEvent);
  }
};

const AnueBottomService = ({ isOnlyChild = false }: { isOnlyChild?: boolean }) => {
  return (
    <AnueItemWrapper isOnlyChild={isOnlyChild}>
      {Tabs.map(({ icon, src, title, gtmEvent }) => {
        const resetImageSize = REST_BUTTONS.has(title) ? { width: NEW_ICON_SIZE, height: NEW_ICON_SIZE } : {};
        return (
          <AnueItem key={title} href={src} onClick={() => onClick(gtmEvent as AnueBottomServiceGtmEvent)}>
            <img src={icon} alt='category-icon' style={resetImageSize} />
            <p>{title}</p>
          </AnueItem>
        );
      })}
    </AnueItemWrapper>
  );
};

const AnueBottomWrapper = ({ isOnlyChild = false }: { isOnlyChild?: boolean }) => {
  if (isOnlyChild) return <AnueBottomService isOnlyChild={isOnlyChild} />;

  return (
    <AsideWrapper title='鉅亨服務' id='modalAnueService' icon={anueServiceIcon}>
      <AnueBottomService />
    </AsideWrapper>
  );
};

export default AnueBottomWrapper;
